import { Component, computed, inject, input } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { distinctUntilChanged, filter } from 'rxjs';
import { TimeInputBoxComponent } from './time-input-box/time-input-box.component';

@Component({
  selector: 'vectronic-formly-time-span',
  imports: [ReactiveFormsModule, TimeInputBoxComponent],
  providers: [{ provide: NG_VALUE_ACCESSOR, multi: true, useExisting: TimeSpanComponent }],
  templateUrl: './time-span.component.html',
  styleUrls: ['./time-span.component.scss'],
})
export class TimeSpanComponent implements ControlValueAccessor {
  fb = inject(FormBuilder);
  protected timeSpanForm = this.fb.group({
    fromHours: this.fb.control<string>('00'),
    fromMinutes: this.fb.control<string>('00'),
    toHours: this.fb.control<string>('00'),
    toMinutes: this.fb.control<string>('00'),
  });
  canSpanOverMidnight = input(false);
  defaultValue = input<string>();
  label = input<string>();

  timeSpanFormChangesSL = toSignal(this.timeSpanForm.valueChanges);

  timeSpanUpdatedCSL = computed(() => {
    const { fromHours, fromMinutes, toHours, toMinutes } = this.timeSpanFormChangesSL();
    return {
      from: { hours: fromHours, minutes: fromMinutes },
      to: { hours: toHours, minutes: toMinutes },
    };
  });

  timeSpanUpdatedNumbersCSL = computed(() => {
    const { from, to } = this.timeSpanUpdatedCSL();
    return {
      from: { hours: parseInt(from.hours), minutes: parseInt(from.minutes) },
      to: { hours: parseInt(to.hours), minutes: parseInt(to.minutes) },
    };
  });

  updateValueCSL = computed(() => {
    const { from, to } = this.timeSpanUpdatedCSL();
    const value = from.hours + `:` + from.minutes + `-` + to.hours + `:` + to.minutes;
    this.onChange(value);
    return this.timeSpanUpdatedCSL();
  });

  fromHoursMaxCSL = computed(() => {
    const { from, to } = this.timeSpanUpdatedNumbersCSL();
    if (!this.canSpanOverMidnight()) {
      return from.minutes < to.minutes ? to.hours : to.hours - 1;
    }
    return 23;
  });

  fromMinutesMaxCSL = computed(() => {
    const { from, to } = this.timeSpanUpdatedNumbersCSL();
    if (!this.canSpanOverMidnight()) {
      return from.hours < to.hours ? 59 : to.minutes - 1;
    }
    return 59;
  });

  toHoursMinCSL = computed(() => {
    const { from, to } = this.timeSpanUpdatedNumbersCSL();
    if (!this.canSpanOverMidnight()) {
      return to.minutes > from.minutes ? from.hours : from.hours + 1;
    }
    return 0;
  });

  toMinutesMinCSL = computed(() => {
    const { from, to } = this.timeSpanUpdatedNumbersCSL();
    if (!this.canSpanOverMidnight()) {
      return to.hours > from.hours ? 0 : from.minutes + 1;
    }
    return 0;
  });

  toHoursMaxCSL = computed(() => (this.timeSpanUpdatedNumbersCSL().to.minutes === 0 ? 24 : 23));
  toMinutesMaxCSL = computed(() => (this.timeSpanUpdatedNumbersCSL().to.hours === 24 ? 0 : 59));

  onChange: (value: string) => void;
  onTouched: () => void;

  constructor() {
    this.getFormControl('toHours')
      .valueChanges.pipe(
        takeUntilDestroyed(),
        distinctUntilChanged(),
        filter((val) => val === '24'),
      )
      .subscribe(() => {
        this.getFormControl('toMinutes').setValue('00');
      });
  }

  writeValue(value: string): void {
    const valueToWrite = value || this.defaultValue() || '00:00-00:00';
    const [from, to] = valueToWrite.split('-');
    const [fromHours, fromMinutes] = from.split(':');
    const [toHours, toMinutes] = to.split(':');
    this.timeSpanForm.setValue({ fromHours, fromMinutes, toHours, toMinutes }, { emitEvent: true });
  }
  registerOnChange(fn: (value: string) => void) {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean) {
    isDisabled ? this.timeSpanForm.disable() : this.timeSpanForm.enable();
  }

  getFormControl(controlName: string) {
    return this.timeSpanForm.get(controlName) as FormControl;
  }
}
